import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowRight, BatteryCharging, Leaf, Lightbulb, Sun, TrendingUp, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const customIcon = L.divIcon({
  className: '',
  iconSize: [28, 40],
  iconAnchor: [14, 40],
  popupAnchor: [0, -38],
  html: `
    <div style="
      width: 28px;
      height: 28px;
      background: #ff8400;
      border: 3px solid #fff;
      border-radius: 50% 50% 50% 0;
      box-shadow: 0 4px 10px rgba(0, 0, 0, 0.28);
      transform: rotate(-45deg);
    ">
      <div style="
        width: 8px;
        height: 8px;
        margin: 7px;
        background: #fff;
        border-radius: 999px;
      "></div>
    </div>
  `,
});

const SOLAR_FARM_COORDS = [-33.24333, 149.05361];

const ZoomAnimator = ({ startZoom, endZoom, duration, isVisible, onComplete }) => {
  const map = useMap();
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!isVisible || hasAnimated.current) return undefined;

    hasAnimated.current = true;
    map.setView(SOLAR_FARM_COORDS, startZoom, { animate: false });

    const handleMoveEnd = () => onComplete();
    const flyTimer = setTimeout(() => {
      map.once('moveend', handleMoveEnd);
      map.flyTo(SOLAR_FARM_COORDS, endZoom, {
        duration: duration / 1000,
        easeLinearity: 0.25,
      });
    }, 300);

    return () => {
      clearTimeout(flyTimer);
      map.off('moveend', handleMoveEnd);
    };
  }, [duration, endZoom, isVisible, map, onComplete, startZoom]);

  return null;
};

const ProjectMarker = ({ isPopupOpen }) => {
  const markerRef = useRef(null);

  useEffect(() => {
    if (isPopupOpen) {
      markerRef.current?.openPopup();
    }
  }, [isPopupOpen]);

  return (
    <Marker ref={markerRef} position={SOLAR_FARM_COORDS} icon={customIcon}>
      <Popup>
        <div className="text-center">
          <strong className="text-[#703aa4]">Yiray Clean Energy Park</strong>
          <br />
          <span className="text-sm text-gray-600">643 Mitchell Highway, Orange NSW</span>
          <br />
          <span className="text-xs text-gray-500">33&deg;14'36"S 149&deg;03'13"E</span>
        </div>
      </Popup>
    </Marker>
  );
};

const useReveal = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(entry.target);
      }
    }, { threshold: 0.1 });

    if (currentRef) observer.observe(currentRef);
    return () => observer.disconnect();
  }, []);

  return [ref, isVisible];
};

const statItems = [
  { label: 'Solar installation', value: '5 MW', icon: <Sun size={20} /> },
  { label: 'Battery storage', value: '10 MWh', icon: <BatteryCharging size={20} /> },
  { label: 'Community raise', value: '$1.35M', icon: <Users size={20} /> },
];

const benefitItems = [
  {
    title: 'Economic Growth',
    icon: <TrendingUp size={38} />,
    description: 'Creating local jobs and stimulating the regional economy through renewable energy investments.',
  },
  {
    title: 'Energy Independence',
    icon: <Sun size={38} />,
    description: 'Reducing reliance on the grid and supporting more stable community energy outcomes.',
  },
  {
    title: 'Environmental Impact',
    icon: <Leaf size={38} />,
    description: 'Reducing carbon emissions and promoting a cleaner, healthier environment.',
  },
  {
    title: 'Community Empowerment',
    icon: <Users size={38} />,
    description: 'Giving people a stake in their energy future through co-operative ownership.',
  },
];

const CentralWest = () => {
  const [heroRef, heroVisible] = useReveal();
  const [heroDividerRef, heroDividerVisible] = useReveal();
  const [mapRef, mapVisible] = useReveal();
  const [howRef, howVisible] = useReveal();
  const [benefitsRef, benefitsVisible] = useReveal();
  const [sharesDividerRef, sharesDividerVisible] = useReveal();
  const [ctaRef, ctaVisible] = useReveal();
  const [isProjectPopupOpen, setIsProjectPopupOpen] = useState(false);
  const openProjectPopup = useCallback(() => setIsProjectPopupOpen(true), []);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 my-8">
      <section
        ref={heroRef}
        className={`overflow-hidden transition-all duration-700 relative ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-[0.9fr_1fr] gap-8 lg:gap-12 items-center">
          <div className={`transition-all duration-700 delay-100 ${heroVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <p className="text-sm font-semibold uppercase tracking-widest text-[#ff8400] mb-3">YCEP</p>
            <h1 className="text-4xl sm:text-5xl text-[#703aa4] leading-tight mb-6">Yiray Clean Energy Park</h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-6">
              A 5 MW solar installation with 10 MWh battery storage and solar tracking technology, developed at 643 Mitchell Highway, Orange NSW.
            </p>
            <div
              className={`mb-5 h-1 bg-[#ff8400] transition-all duration-700 delay-500 origin-left ${heroVisible ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'}`}
              aria-hidden="true"
            />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {statItems.map((item) => (
                <div key={item.label} className="py-3 text-center">
                  <div className="flex items-center justify-center gap-2 text-[#ff8400] mb-1">
                    {item.icon}
                    <span className="text-sm">{item.label}</span>
                  </div>
                  <p className="text-2xl text-gray-800">{item.value}</p>
                </div>
              ))}
            </div>
          </div>

          <div className={`overflow-hidden min-h-[420px] transition-all duration-700 delay-300 ${heroVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <MapContainer
              center={SOLAR_FARM_COORDS}
              zoom={6}
              style={{ height: '100%', minHeight: '420px', width: '100%' }}
              scrollWheelZoom={false}
              dragging={false}
              doubleClickZoom={false}
              touchZoom={false}
              zoomControl={false}
              boxZoom={false}
              keyboard={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <ZoomAnimator
                startZoom={6}
                endZoom={15}
                duration={2500}
                isVisible={heroVisible}
                onComplete={openProjectPopup}
              />
              <ProjectMarker isPopupOpen={isProjectPopupOpen} />
            </MapContainer>
          </div>
        </div>
      </section>

      <div
        ref={heroDividerRef}
        className={`flex justify-center my-12 transition-all duration-500 ${heroDividerVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`}
      >
        <img src="/media/logo-icon.svg" alt="" className="w-10 h-10" aria-hidden="true" />
      </div>

      <section
        ref={howRef}
        className={`mb-20 grid grid-cols-1 lg:grid-cols-[0.9fr_1fr] gap-8 lg:gap-12 items-center transition-all duration-700 ${howVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className={`order-2 lg:order-1 transition-all duration-700 delay-100 ${howVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
          <img
            src="/media/main-svg.svg"
            alt="Community energy illustration"
            className="w-full h-auto object-contain p-0 sm:p-2 lg:h-full lg:min-h-[420px] lg:p-6"
          />
        </div>
        <div className={`order-1 lg:order-2 transition-all duration-700 delay-300 ${howVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
          <p className="text-sm font-semibold uppercase tracking-widest text-[#ff8400] mb-3">How it works</p>
          <h2 className="text-3xl sm:text-4xl text-[#703aa4] leading-tight mb-5">Community ownership in a working energy asset</h2>
          <p className="text-gray-600 text-xl mb-4 leading-relaxed">
            The ED Central West NSW Renewable Energy Co-operative successfully reached the community fundraising goal of $1.35 Million to secure partial ownership of the Yiray Clean Energy Park. This achievement ensures maximum possible community ownership before the park is commissioned in June 2026.
          </p>
          <p className="text-gray-600 text-xl leading-relaxed">
            The electricity generated by the park is fed into the National Grid, with excess power used to recharge a battery for later use or sold when electricity prices peak.
          </p>
        </div>
      </section>

      <section
        ref={benefitsRef}
        className={`mb-12 transition-all duration-700 ${benefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div>
          <h2 className={`text-3xl text-gray-800 text-center leading-tight mb-8 transition-all duration-700 delay-100 ${benefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>Benefits for Our Community</h2>

          <div className="grid grid-cols-1 md:grid-cols-4">
            {benefitItems.map((item, index) => (
              <div
                key={item.title}
                className={`py-6 md:px-6 md:first:pl-0 md:last:pr-0 border-b md:border-b-0 md:border-r last:border-b-0 md:last:border-r-0 border-gray-300 transition-all duration-500 ${benefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: benefitsVisible ? `${150 + index * 100}ms` : '0ms' }}
              >
                <div className="text-[#703aa4] mb-4">{item.icon}</div>
                <h3 className="text-xl font-semibold leading-tight mb-3 text-gray-800">{item.title}</h3>
                <p className="text-xl text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>

          <div className={`pt-8 mt-6 border-t-4 border-[#ff8400] flex flex-col md:flex-row items-start md:items-center gap-6 text-left transition-all duration-500 delay-[600ms] ${benefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex-shrink-0">
              <Lightbulb className="text-[#ff8400]" size={48} />
            </div>
            <div>
              <h3 className="text-xl font-semibold leading-tight mb-2">Electrify 2800</h3>
              <p className="text-xl text-gray-600 leading-relaxed">
                Electrify 2800 is a community initiative based in Orange, New South Wales, working to transition the 2800 postcode area to clean, renewable energy. The group focuses on supporting households, businesses, and local organisations to adopt solar power, improve energy efficiency, and explore innovative solutions such as battery storage and electric transport. Its goal is to cut carbon emissions, reduce energy costs, and build a more sustainable future for the region. Find out more on the{' '}
                <a
                  href="https://electrify2800.org.au/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#703aa4] underline hover:text-[#ff8400] transition duration-300"
                >
                  Electrify 2800 website
                </a>.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div
        ref={sharesDividerRef}
        className={`flex justify-center my-12 transition-all duration-500 ${sharesDividerVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`}
      >
        <img src="/media/logo-icon.svg" alt="" className="w-10 h-10" aria-hidden="true" />
      </div>

      <section
        ref={mapRef}
        className={`mb-12 transition-all duration-700 ${mapVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className={`transition-all duration-700 delay-100 ${mapVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-sm font-semibold uppercase tracking-widest text-[#ff8400] mb-3">Shares</p>
          <h2 className="text-3xl text-[#703aa4] leading-tight mb-5">Current share status</h2>
          <p className="text-gray-600 text-xl mb-4 leading-relaxed">
            The average payback is estimated at up to 8.6 years, providing a return on investment of 8 - 12% per annum.
          </p>
          <p className="text-gray-600 text-xl mb-4 leading-relaxed">
            Applications for new members are closed and new shares can no longer be issued. The transfer or sale of shares from an existing member to another existing member, or to a new member, will be possible during the construction and operational periods.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 text-[#703aa4] font-semibold hover:text-[#ff8400] transition duration-300"
          >
            Submit an expression of interest
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <section
        ref={ctaRef}
        className={`mt-8 mb-0 text-left transition-all duration-700 relative overflow-hidden ${ctaVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'}`}
      >
        <div className="relative z-10 grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-6 md:gap-8 items-center">
          <div>
            <h2 className={`text-3xl text-[#703aa4] leading-tight mb-4 transition-all duration-700 delay-200 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>Stay close to the Yiray project</h2>
            <p className={`text-xl leading-relaxed text-gray-600 transition-all duration-700 delay-300 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              While initial investments are now closed, you can subscribe to stay informed about project progress and future opportunities.
            </p>
          </div>
          <div
            className={`hidden md:block w-1 h-full bg-[#ff8400] transition-all duration-700 delay-300 ${ctaVisible ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-75'}`}
            aria-hidden="true"
          />
          <div className={`transition-all duration-700 delay-400 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <a
              href="https://energydemocracy.us14.list-manage.com/subscribe?u=531274272281a368367bd1700&id=ab566fa072"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-[#ff8400] hover:bg-[#e67600] text-white text-xl font-semibold shadow-md py-2 px-6 rounded-full transition duration-300"
            >
              Subscribe to our newsletter
            </a>
          </div>
        </div>
      </section>

      <div className="mb-8 mt-8 pt-8 border-t border-gray-200">
        <hr className="border-t border-gray-600 w-full mb-4" />
        <p className="text-sm text-gray-600">
          * Please read the <a href="/media/Energy%20Democracy%20Disclosure%20Statement-2e79b172bc661b833ded6066d87fd44f.pdf" target="_blank" rel="noreferrer" className="text-[#703aa4] underline">Disclosure Statement</a>. No application for relief under section 741 of the Corporations Act 2001 (Cth) will be sought by the Energy Democracy Central West NSW Co-operative Limited ('the Co-operative') in relation to any statement made in this document as the Chapter 6D disclosure provisions do not apply to members' shares in the Co-operative.
        </p>
      </div>
    </div>
  );
};

export default CentralWest;
